import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'username', label: 'User', sortable: false },
    { key: 'email', sortable: false },
    { key: 'plan', label: 'Subscription', sortable: false },
    { key: 'enabled', label: 'Status', sortable: false },
    { key: 'time', label: 'Created At', sortable: false },
    { key: 'actions' }
  ]
  const perPage = ref(50)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [50, 100, 500, 1000]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const courtFilter = ref(null)
  const planStatusFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value
      ? refUserListTable.value.localItems.length
      : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch(
    [
      currentPage,
      perPage,
      searchQuery,
      courtFilter,
      planStatusFilter,
      planFilter,
      statusFilter
    ],
    () => {
      refetchData()
    }
  )

  const fetchUsers = (ctx, callback) => {
    try {
      let queryParams = {
        perPage: perPage.value,
        page: currentPage.value
      }

      if (searchQuery.value) {
        queryParams['query'] = searchQuery.value
      }

      if (courtFilter.value) {
        queryParams['filters[court_id]'] = courtFilter.value
      }

      if (statusFilter.value) {
        queryParams['filters[enabled]'] = statusFilter.value
      }
      if (planStatusFilter.value) {
        queryParams['filters[plan_status]'] = planStatusFilter.value
      }

      if (planFilter.value) {
        queryParams['filters[plan.reference_id]'] = planFilter.value
      }

      store
        .dispatch('userStore/fetchUsers', queryParams)
        .then((response) => {
          callback(response.data)
          totalUsers.value = response.meta.total
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching users list',
              icon: 'AlertTriangleIcon',
              variant: 'danger'
            }
          })
        })
    } catch (e) {
      console.log(e)
    }
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = (status) => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchUsers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    courtFilter,
    planStatusFilter,
    planFilter,
    statusFilter
  }
}
